import React from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
// data
import {
  hasQuantityAndDescription,
  mapBoolToString,
  pageInfo,
  seedValuesWithSelected
} from './data/utils';
import onEventFaqSubmit from './data/onEventFaqSubmit';
import { eventFaqDefinition } from '../shared-components/faq/data/eventFaqDefinition';
import { onEventFaqBlur, onEventFaqChange } from './data/onEventFaqChange';
import { EVENT_FAQ_CONST, FOOD_BEVERAGE_FORM, EVENT_FAQ_ANSWERS_CONST } from '../shared-components/faq/data/constants';
// faq component
import RadioField from '../app-main/components/reduxForms/fields/RadioField/RadioField';
import ButtonNavigation from '../app-faq-components/ButtonNavigation';
import ToggleFieldRow from '../app-faq-components/ToggleFieldRow';
import FaqHeader from '../app-faq-components/FaqHeader';
// shared component
import TextAreaField from '../shared-components/reduxForms/fields/TextAreaField/TextAreaField';
const {
  FOOD_BEVERAGE_RULES,
  FOOD_PREP_RULES,
  FULL_KITCHEN,
  COMMERCIAL_KITCHEN,
  KITCHENETTE,
  PREP_AREA,
  PLACE_SETTINGS,
  SERVING_UTENSILS,
  CUPS_GLASSES,
  WINE_GLASSES
} = EVENT_FAQ_CONST;
const {
  OUTSIDE_FOOD_BEVERAGE_ALLOWED,
  ON_SITE_FOOD_BEVERAGE,
  LICENSED_FOOD_BEVERAGE,
  OTHER_FOOD_AND_BEVERAGE_RULES,
  NO_OUTSIDE_FOOD_BEVERAGE,
  COOKING_AND_PREP_ALLOWED,
  ONLY_FOOD_PREP,
  NO_COOKING_ALLOWED,
  OTHER_FOOD_PREP_RULES
} = EVENT_FAQ_ANSWERS_CONST;
const FoodBeverageForm = ({
  error,
  handleSubmit,
  intl,
  page,
  hasOtherFoodAndBeverageRules,
  foodAndBeverageRulesAnswer,
  hasOtherFoodPrepRules,
  foodPrepRulesAnswer,
  spaceId,
  accessToken,
  showErrorToast,
  ...props
}) => {
  const foodAndBeverages = get(eventFaqDefinition, 'food_and_beverage', {});
  const header = intl.formatMessage(pageInfo(page).header);
  const Icon = pageInfo(page).icon;
  const onChangeFn = onEventFaqChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onBlurFn = onEventFaqBlur(props.dispatch, spaceId, accessToken, showErrorToast);
  return (
    <form className="flex-basis-100" onSubmit={handleSubmit}>
      <FaqHeader header={header} icon={<Icon />} />
      <div>
        <div className="h6 questions-section">
          <FormattedMessage
            id="hosting.food_and_beverage_form.fnb_rules_header"
            defaultMessage="Rules for food and non-alcoholic beverages"
          />
        </div>
        <Field
          component={RadioField}
          name={FOOD_BEVERAGE_RULES}
          className="flex baseline column padding-xl-bottom margin-xl-top flex-wrap border-bottom-gray-200"
          componentClassName="w-100"
          onChange={onChangeFn}
          options={
            [
              OUTSIDE_FOOD_BEVERAGE_ALLOWED,
              ON_SITE_FOOD_BEVERAGE,
              LICENSED_FOOD_BEVERAGE,
              NO_OUTSIDE_FOOD_BEVERAGE,
              OTHER_FOOD_AND_BEVERAGE_RULES
            ].map((item) => (
              {
                id: item,
                value: item,
                label: intl.formatMessage(foodAndBeverages[FOOD_BEVERAGE_RULES][item]),
                ...((hasOtherFoodAndBeverageRules && item === OTHER_FOOD_AND_BEVERAGE_RULES) &&
                  {
                    component: (
                      <div className="margin-lg-top" style={{ marginLeft: '-1.5rem' }}>
                        <Field
                          name={`${FOOD_BEVERAGE_RULES}_description`}
                          component={TextAreaField}
                          attributes={{
                            'data-testing-id': OTHER_FOOD_AND_BEVERAGE_RULES
                          }}
                          onBlur={onBlurFn}
                          placeholder={intl.formatMessage(foodAndBeverages[FOOD_BEVERAGE_RULES][OTHER_FOOD_AND_BEVERAGE_RULES].placeholder)}
                          maxLength={1000}
                        />
                      </div>
                    )
                  }
                )
              }
            ))}
        />
      </div>
      <div>
        <div className="h6 questions-section">
          <FormattedMessage
            id="hosting.food_and_beverage_form.food_prep_rules_header"
            defaultMessage="Rules for cooking and food preparation"
          />
        </div>
        <Field
          component={RadioField}
          name={FOOD_PREP_RULES}
          className="flex baseline column padding-xl-bottom margin-xl-top flex-wrap border-bottom-gray-200"
          componentClassName="w-100"
          onChange={onChangeFn}
          options={
            [
              COOKING_AND_PREP_ALLOWED,
              ONLY_FOOD_PREP,
              NO_COOKING_ALLOWED,
              OTHER_FOOD_PREP_RULES
            ].map((item) => (
              {
                id: item,
                value: item,
                label: intl.formatMessage(foodAndBeverages[FOOD_PREP_RULES][item]),
                ...((hasOtherFoodPrepRules && item === OTHER_FOOD_PREP_RULES) &&
                {
                  component: (
                    <div className="margin-lg-top" style={{ marginLeft: '-1.5rem' }}>
                      <Field
                        name={`${FOOD_PREP_RULES}_description`}
                        component={TextAreaField}
                        attributes={{
                          'data-testing-id': OTHER_FOOD_PREP_RULES
                        }}
                        onBlur={onBlurFn}
                        placeholder={intl.formatMessage(foodAndBeverages[FOOD_PREP_RULES][OTHER_FOOD_PREP_RULES].placeholder)}
                        maxLength={1000}
                      />
                    </div>
                  )
                })
              }
            ))
          }
        />
      </div>
      <div>
        <h6 className="questions-section">
          <FormattedMessage
            id="hosting.event_rules_form.kitchen_setup_header"
            defaultMessage="Available kitchen setups"
          />
        </h6>
        {
          Object.keys(foodAndBeverages.kitchen_setups_provided).map((key) => (
            <>
              <div>
                <ToggleFieldRow
                  label={foodAndBeverages.kitchen_setups_provided[key]}
                  formName={FOOD_BEVERAGE_FORM}
                  fieldName={key}
                  hasQuantity={!!props[`${key}_quantity`]}
                  hasDescription={!!props[`${key}_description`]}
                  dispatch={props.dispatch}
                  onChange={onChangeFn}
                  onBlur={onBlurFn}
                />
              </div>
              <hr />
            </>
          ))
        }
      </div>
      <div>
        <h6 className="questions-section">
          <FormattedMessage
            id="hosting.event_rules_form.kitchen_amenities_header"
            defaultMessage="Kitchen Amenities"
          />
        </h6>
        {
          Object.keys(foodAndBeverages.kitchen_amenities_provided).map((key) => (
            <>
              <div>
                <ToggleFieldRow
                  label={foodAndBeverages.kitchen_amenities_provided[key]}
                  formName={FOOD_BEVERAGE_FORM}
                  fieldName={key}
                  dispatch={props.dispatch}
                  hasQuantity={props[`${key}_quantity`]}
                  hasDescription={props[`${key}_description`]}
                  subheader={foodAndBeverages.kitchen_amenities_provided[key].subheader}
                  tooltip={foodAndBeverages.kitchen_amenities_provided[key].tooltip}
                  onChange={onChangeFn}
                  onBlur={onBlurFn}
                />
              </div>
              <hr />
            </>
          ))
        }
      </div>
      <div className="text-danger text-small margin-xl-bottom">{error}</div>
      <ButtonNavigation {...props} spaceId={spaceId} />
    </form>
  );
};

FoodBeverageForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  dispatch: PropTypes.func,
  parkingToggleFieldValue: PropTypes.string,
  intl: intlShape,
  spaceId: PropTypes.string,
  accessToken: PropTypes.string,
  showErrorToast: PropTypes.bool,
  page: PropTypes.string,
  hasOtherFoodAndBeverageRules: PropTypes.bool,
  foodAndBeverageRulesAnswer: PropTypes.string,
  hasOtherFoodPrepRules: PropTypes.bool,
  foodPrepRulesAnswer: PropTypes.string
};

const config = {
  form: FOOD_BEVERAGE_FORM,
  onSubmit: onEventFaqSubmit
};

const selector = formValueSelector(FOOD_BEVERAGE_FORM);

function mapStateToProps (state, { eventFaq }) {
  const foodAndBeverageRulesKeys = new Set([
    ON_SITE_FOOD_BEVERAGE,
    OUTSIDE_FOOD_BEVERAGE_ALLOWED,
    LICENSED_FOOD_BEVERAGE,
    NO_OUTSIDE_FOOD_BEVERAGE
  ]);
  const foodPrepRulesKeys = new Set([
    COOKING_AND_PREP_ALLOWED,
    ONLY_FOOD_PREP,
    NO_COOKING_ALLOWED
  ]);
  const kitchenSetupAndAmenityFields = [
    FULL_KITCHEN,
    COMMERCIAL_KITCHEN,
    KITCHENETTE,
    PREP_AREA,
    PLACE_SETTINGS,
    SERVING_UTENSILS,
    CUPS_GLASSES,
    WINE_GLASSES
  ];
  const hasOtherFoodBeverageRules = eventFaq.hasOwnProperty(FOOD_BEVERAGE_RULES) && !foodAndBeverageRulesKeys.has(eventFaq[FOOD_BEVERAGE_RULES]);
  const hasOtherFoodPrepRules = eventFaq.hasOwnProperty(FOOD_PREP_RULES) && !foodPrepRulesKeys.has(eventFaq[FOOD_PREP_RULES]);

  // if values are freeform then seed `textarea` initial data
  const initialValues = {
    ...mapBoolToString(seedValuesWithSelected(eventFaq)),
    [`${FOOD_BEVERAGE_RULES}_description`]: hasOtherFoodBeverageRules ? eventFaq[FOOD_BEVERAGE_RULES] : '',
    ...(hasOtherFoodBeverageRules && { [`${FOOD_BEVERAGE_RULES}`]: OTHER_FOOD_AND_BEVERAGE_RULES }),
    [`${FOOD_PREP_RULES}_description`]: hasOtherFoodPrepRules ? eventFaq[FOOD_PREP_RULES] : '',
    ...(hasOtherFoodPrepRules && { [`${FOOD_PREP_RULES}`]: OTHER_FOOD_PREP_RULES })
  };
  const foodAndBeverageRulesSelector = selector(state, `${FOOD_BEVERAGE_RULES}`) === OTHER_FOOD_AND_BEVERAGE_RULES;
  const foodPrepRulesSelector = selector(state, `${FOOD_PREP_RULES}`) === OTHER_FOOD_PREP_RULES;
  const itemSelected = hasQuantityAndDescription(kitchenSetupAndAmenityFields, selector, state);

  return {
    hasOtherFoodAndBeverageRules: foodAndBeverageRulesSelector,
    hasOtherFoodPrepRules: foodPrepRulesSelector,
    ...itemSelected,
    initialValues
  };
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(FoodBeverageForm)));
